import React, { useState, useEffect } from 'react';
import { AlertCircle, AlertTriangle, Check, Dices, Globe, Link2, Loader2, Plus, Search, X } from 'lucide-react';
import { useChatbot } from '../../../contexts/ChatbotContext';
import { useAISettings } from '../../../hooks/useAISettings';
import { useKnowledgeBase } from '../../../hooks/useKnowledgeBase';
import { extractMainDomain, isProbablyScrapable } from '../../../utils/urlUtils';
import { motion, AnimatePresence } from 'framer-motion';

interface UrlScraperProps {
  isLimitExceeded?: boolean;
}

export function UrlScraper({ isLimitExceeded }: UrlScraperProps) {
  const { chatbotPublicId } = useChatbot();
  const { addKnowledge } = useAISettings();
  const { scrapeUrl, loading, error, clearError } = useKnowledgeBase();

  const [url, setUrl] = useState('');
  const [validUrl, setValidUrl] = useState(false);
  const [isExtended, setIsExtended] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [preview, setPreview] = useState<{ title: string; content: string; url: string } | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [isScrapeRecommended, setIsScrapeRecommended] = useState(true);

  // Valider l'URL en temps réel
  useEffect(() => {
    try {
      const urlObj = new URL(url);
      setValidUrl(urlObj.protocol === 'http:' || urlObj.protocol === 'https:');
      setIsScrapeRecommended(isProbablyScrapable(url));
    } catch {
      setValidUrl(false);
      setIsScrapeRecommended(true);
    }
  }, [url]);

  // Masquer le message de succès après un délai
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  // Générer un exemple d'URL basé sur un ensemble de sites populaires
  const generateExampleUrl = () => {
    const examples = [
      'https://www.wikipedia.org',
      'https://www.lemonde.fr',
      'https://www.bbc.com/news',
      'https://www.nytimes.com',
      'https://www.01net.com',
      'https://blog.hubspot.com',
      'https://www.nature.com',
      'https://fr.wikipedia.org/wiki/Intelligence_artificielle',
      'https://developer.mozilla.org/fr/',
    ];
    const example = examples[Math.floor(Math.random() * examples.length)];
    setUrl(example);
  };

  // Prévisualiser le contenu
  const handlePreview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validUrl || !url.trim() || isLimitExceeded) return;
    
    clearError();
    setStatusMessage('Analyse de la page en cours...');
    setShowPreview(false);
    setPreview(null);
    
    try {
      const scrapedData = await scrapeUrl(url);
      if (scrapedData) {
        setPreview({
          ...scrapedData,
          url: url // Assurez-vous que l'URL est incluse
        });
        setShowPreview(true);
        setStatusMessage('Prévisualisation disponible.');
      }
    } catch (err) {
      setStatusMessage('Échec de l\'analyse de la page.');
    }
  };

  // Ajouter l'URL à la base de connaissances
  const handleAddUrl = async () => {
    if (!chatbotPublicId || !validUrl || isLimitExceeded || !preview) return;
    
    try {
      await addKnowledge({
        type: 'url',
        name: preview.title || extractMainDomain(url),
        url: url,
        content: preview.content
      }, chatbotPublicId);
      
      setUrl('');
      setShowPreview(false);
      setPreview(null);
      setShowSuccess(true);
      setStatusMessage('Page ajoutée avec succès!');
    } catch (err) {
      setStatusMessage('Échec de l\'ajout de la page.');
    }
  };

  // Réinitialiser le formulaire
  const handleReset = () => {
    setUrl('');
    setShowPreview(false);
    setPreview(null);
    clearError();
    setStatusMessage('');
  };

  // Obtenir des classes conditionnelles pour le champ d'URL
  const getUrlInputClasses = () => {
    let baseClasses = "flex-1 bg-white/5 border rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 transition-all";
    
    if (isLimitExceeded) {
      return `${baseClasses} border-red-500/50 focus:ring-red-500/30`;
    }
    
    if (!url) {
      return `${baseClasses} border-white/10 focus:ring-indigo-500`;
    }
    
    if (validUrl) {
      return `${baseClasses} border-green-500/30 focus:ring-green-500`;
    }
    
    return `${baseClasses} border-yellow-500/30 focus:ring-yellow-500`;
  };

  // Estimer le temps de scraping basé sur la complexité de l'URL
  const getScrapingTimeEstimate = () => {
    if (!validUrl) return '';
    
    try {
      const urlObj = new URL(url);
      const isSimple = urlObj.pathname === '/' || urlObj.pathname === '';
      const hasParams = urlObj.search.length > 0;
      
      if (isSimple && !hasParams) return 'Estimation: ~5 secondes';
      if (hasParams) return 'Estimation: ~15-30 secondes';
      return 'Estimation: ~10-20 secondes';
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Globe className="w-4 h-4 text-indigo-400" />
          Importer depuis une URL
        </h4>
        <button
          type="button"
          onClick={() => setIsExtended(!isExtended)}
          className="text-xs text-indigo-400 hover:underline focus:outline-none transition-colors"
        >
          {isExtended ? "Options simples" : "Options avancées"}
        </button>
      </div>

      <form onSubmit={handlePreview} className="space-y-3">
        <div className="flex flex-col space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1 group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Link2 className={`w-4 h-4 transition-colors ${validUrl ? 'text-green-400' : 'text-gray-400'}`} />
              </div>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className={`${getUrlInputClasses()} pl-10`}
                disabled={isLimitExceeded || loading}
              />
              {url && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  type="button"
                  onClick={handleReset}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
                >
                  <X className="w-4 h-4" />
                </motion.button>
              )}
              {validUrl && !isScrapeRecommended && (
                <div className="absolute -bottom-6 left-0 text-xs text-yellow-400">
                  Ce site pourrait être difficile à analyser (contenu dynamique ou restrictions)
                </div>
              )}
            </div>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={generateExampleUrl}
              className="px-2 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
              title="Générer un exemple d'URL"
              disabled={loading || isLimitExceeded}
            >
              <Dices className="w-5 h-5" />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={!validUrl || loading || isLimitExceeded}
              className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {isExtended ? "Analyser" : "Analyser la page"}
            </motion.button>
          </div>

          <AnimatePresence>
            {isExtended && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap items-center gap-3 mt-2 p-3 bg-white/5 rounded-lg">
                  <div className="text-sm text-gray-300">
                    Conseils pour un meilleur scraping:
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300">Pages statiques</span>
                    <span className="px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300">Pages d'information</span>
                    <span className="px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300">Pages "À propos"</span>
                    <span className="px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300">Blogs</span>
                    <span className="px-2 py-1 rounded-full bg-indigo-500/10 text-indigo-300">Documentation</span>
                  </div>
                  
                  {validUrl && (
                    <div className="w-full mt-2 text-xs text-gray-400">
                      {getScrapingTimeEstimate()}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Affichage des erreurs et des statuts */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 flex items-start gap-2 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Erreur</p>
                  <p>{error}</p>
                </div>
              </div>
            </motion.div>
          )}
          
          {showSuccess && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Succès</p>
                  <p>Page ajoutée à la base de connaissances avec succès</p>
                </div>
              </div>
            </motion.div>
          )}
          
          {isLimitExceeded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">Limite de caractères dépassée</p>
                  <p>Vous ne pouvez pas ajouter plus de contenu. Supprimez quelques éléments pour continuer.</p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Statut de chargement */}
        <AnimatePresence>
          {loading && statusMessage && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-3 py-2 bg-gray-500/10 border border-gray-500/30 rounded-lg text-gray-300 flex items-center gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                <p>{statusMessage}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Prévisualisation du contenu */}
        <AnimatePresence>
          {showPreview && preview && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 space-y-4"
            >
              <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10 overflow-hidden">
                <h3 className="font-medium text-white text-lg mb-2">{preview.title}</h3>
                <div className="text-gray-300 text-sm mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-indigo-400" />
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:underline truncate">
                    {url}
                  </a>
                </div>
                
                <div className="max-h-64 overflow-y-auto bg-black/20 rounded-lg p-3 text-gray-300 text-sm">
                  <div className="whitespace-pre-wrap line-clamp-6">
                    {preview.content}
                  </div>
                  <div className="mt-2 text-xs text-gray-400 flex justify-between items-center">
                    <span>
                      {preview.content.length.toLocaleString()} caractères ({Math.round(preview.content.length / 6).toLocaleString()} mots environ)
                    </span>
                    {preview.content.length > 2000 && (
                      <span className="text-yellow-400">Contenu volumineux</span>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="button"
                    onClick={handleAddUrl}
                    disabled={isLimitExceeded}
                    className="px-4 py-2 rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Ajouter à la base
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
    </div>
  );
} 